import { useEffect, useState } from "react";
import { Bot, Send } from "lucide-react";
import api from "../../lib/api";
import { Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";

export default function Inbox() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);

  const loadThreads = () =>
    api
      .get("/owner/inbox")
      .then(({ data }) => setThreads(data))
      .catch(() => setThreads([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    loadThreads();
  }, []);

  const openThread = (thread) => {
    setActive(thread);
    api
      .get(`/owner/inbox/${thread.customer.id}`)
      .then(({ data }) => setMessages(data.messages))
      .catch(() => setMessages([]));
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    await api.post(`/owner/inbox/${active.customer.id}/reply`, { body: reply });
    setReply("");
    openThread(active);
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">WhatsApp inbox</h1>
        <Badge variant="primary"><Bot className="h-3 w-3" /> AI assistant placeholder</Badge>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Connects to the WhatsApp Business Platform once live credentials are configured — see{" "}
        <code className="rounded bg-muted px-1">app/Services/WhatsApp</code>. Until then, messages sent
        here are logged, not delivered.
      </p>

      <div className="grid grid-cols-1 gap-0 rounded-2xl border border-border md:grid-cols-[280px_1fr]" style={{ minHeight: 420 }}>
        <div className="max-h-64 divide-y divide-border overflow-y-auto border-b border-border md:max-h-none md:border-b-0 md:border-r">
          {loading ? (
            <p className="p-4 text-sm text-muted-foreground">Loading…</p>
          ) : threads.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No conversations yet.</p>
          ) : (
            threads.map((t) => (
              <button
                key={t.customer.id}
                onClick={() => openThread(t)}
                className={`block w-full p-3 text-left hover:bg-muted ${active?.customer.id === t.customer.id ? "bg-muted" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-900">{t.customer.full_name}</p>
                  {t.unread_count > 0 && <Badge variant="primary">{t.unread_count}</Badge>}
                </div>
                <p className="truncate text-xs text-muted-foreground">{t.last_message}</p>
              </button>
            ))
          )}
        </div>

        <div className="flex h-[420px] flex-col p-4 md:h-auto">
          {!active ? (
            <p className="text-sm text-muted-foreground">Select a conversation.</p>
          ) : (
            <>
              <div className="flex-1 space-y-2 overflow-y-auto">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                      m.direction === "outbound" ? "ml-auto bg-primary text-white" : "bg-muted text-slate-800"
                    }`}
                  >
                    {m.message_body}
                    {m.is_ai_generated && (
                      <p className="mt-1 text-[10px] opacity-70">AI-drafted reply</p>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendReply()}
                  placeholder="Type a reply…"
                />
                <Button onClick={sendReply}><Send className="h-4 w-4" /></Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
